import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import PopUp from 'shared/components/pop-up/pop-up';
import SearchBar from 'shared/components/search-bar/search-bar';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';

import { useConnectorsContextProvider } from 'features/connectors/context/connectors-provider';
import useConnectorOrganisation from 'features/connectors/hooks/connector-settings-organisation-hook';
import useLocationsModal from 'features/connectors/views/connector-details/connector-settings/locations-modal/hooks/locations-modal-hook';
import styles from 'features/connectors/views/connector-details/connector-settings/locations-modal/locations-modal.module.scss';
import OrganisationUnit from 'features/organisation/domain/models/organisation-unit';
import LocationRow from '../../../../components/location-row.component';

const LocationsModal = () => {
  const { t } = useTranslation('connectors');

  const { connectorDetailsHook } = useConnectorsContextProvider();
  const {
    selectedLocations,
    readLocationsIsLoading,
    readLocationsIsSuccess,
    locations,
    searchBarValue,
    onLocationSelected,
    onLocationDeselected,
    onSearchValueChanged,
  } = useConnectorOrganisation();

  const { showEditErrorPopup, hidePopup, handleOnSaveButtonClicked, handleOnClose } = useLocationsModal({ selectedLocations });

  const columns: ColumnDefinition<OrganisationUnit, NestedKeyof<OrganisationUnit>>[] = [
    {
      key: 'name',
      label: t('locationsModal.column.name'),
      disableSort: true,
      renderCustomContentProvider: (location) => <LocationRow name={location.name} />,
    },
  ];

  const selectedRows = locations
    .filter((x) => selectedLocations.some((y) => y.id === x.id))
    .map<RowItem<OrganisationUnit>>((locations) => ({
      data: locations,
    }));

  return (
    <Dialog
      className={styles.modal}
      open={connectorDetailsHook.isLocationPickerOpen}
      onClose={handleOnClose}
      slotProps={{
        paper: {
          classes: { root: styles.locationsModalPaper },
          sx: { overflow: 'visible' },
        },
      }}
    >
      <div className={styles.container}>
        <div className={styles.headerContainer}>
          <Typography className={styles.headerTitle} variant="h3">
            {t('locationsModal.title')}
          </Typography>
          <div className={styles.headerActionsContainer}>
            <Button
              className={styles.linkButton}
              variant="contained"
              onClick={handleOnSaveButtonClicked}
              startIcon={<CheckIcon className={styles.checkIcon} />}
              data-testid="locationsModalSaveButton"
            >
              {t('locationsModal.saveButtonText')}
            </Button>
            <IconButton aria-label="close" className={styles.closeButton} onClick={handleOnClose}>
              <Close />
            </IconButton>
          </div>
        </div>
        <div className={styles.searchBarContainer}>
          <SearchBar searchValue={searchBarValue} onSearchValueChanged={onSearchValueChanged} />
        </div>
        {readLocationsIsLoading && <LoadingIndicator />}
        {readLocationsIsSuccess && locations && (
          <Table
            className={styles.heading}
            data={locations.map<RowItem<OrganisationUnit>>((locations) => ({
              data: locations,
            }))}
            columns={columns}
            rowsAreSelectable
            onRowDeselected={onLocationDeselected}
            onRowSelected={onLocationSelected}
            rowIdentifier={(item: OrganisationUnit) => item.id}
            selectedRows={selectedRows}
          />
        )}
      </div>
      <PopUp
        isOpen={showEditErrorPopup}
        title={t('locationsModal.saveEditErrorPopup.editTitle')}
        body={t('locationsModal.saveEditErrorPopup.unlinkElectronicClientRecord')}
        primaryButtonText={t('locationsModal.saveEditErrorPopup.primaryButtonText')}
        handleOnClose={() => hidePopup()}
        primaryButtonAction={() => hidePopup()}
      />
    </Dialog>
  );
};

export default LocationsModal;
